import React, {useState, useEffect, useRef, createRef} from 'react';
import clsx from 'clsx';
import ArrIconButton from '../arr-icon-button/ArrIconButton';
import './ArrNavigationList.scss';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';

export interface NavigationListItem {
  id: string;
  label: string;
  route?: string;
  disabled?: boolean;
  canViewAction: boolean;
  actionIcon?: string;
  isCustomReport?: boolean;
  isShared?: boolean;
}

import {SVGProps} from 'react';

export interface NavigationListProps {
  items: NavigationListItem[];
  onNavigate?: (item: NavigationListItem) => void;
  onAction?: (itemId: string) => void;
  actionLabel: string;
  actionIcon: React.ReactElement<SVGProps<SVGSVGElement>>;
  useLabelForTestId?: boolean;
  dataTestId: string;
}

const ArrNavigationList: React.FC<NavigationListProps> = ({
  items,
  onNavigate,
  onAction,
  actionLabel,
  actionIcon,
  useLabelForTestId = false,
  dataTestId,
}) => {
  const [focusedIndex, setFocusedIndex] = useState(0);

  // Create refs only once using useRef, not every render
  const refs = useRef<React.RefObject<HTMLLIElement | null>[]>([]);

  // Create refs only once
  if (refs?.current?.length !== items?.length) {
    refs.current = items?.map(() => createRef<HTMLLIElement>());
  }

  useEffect(() => {
    refs?.current[focusedIndex]?.current?.focus();
  }, [focusedIndex]);

  const getNextEnabledIndex = (start: number, direction: 1 | -1) => {
    let newIndex = start;
    const len = items.length;

    for (let i = 0; i < len; i++) {
      newIndex = (newIndex + direction + len) % len;
      if (!items[newIndex]?.disabled) return newIndex;
    }
    return start; // fallback if all are disabled
  };
  const handleRowClick = (item: NavigationListItem, e: React.MouseEvent) => {
    if (item?.disabled) return;
    // Prevent icon clicks from triggering row navigation
    if ((e.target as HTMLElement).closest('.arr-navlist__icon')) return;
    onNavigate?.(item);
  };

  return (
    <ul
      className="arr-navlist"
      role="list"
      data-testid={`arr-navigation-list-ul-${dataTestId}`}
    >
      {items?.map((item, index) => (
        <li
          key={item?.id}
          className={clsx('arr-navlist__item', {
            'arr-navlist__item--disabled': item?.disabled,
          })}
          data-testid={
            useLabelForTestId
              ? item.label.replaceAll(' ', '-').toLowerCase() + '-list-item'
              : `list-item-${index}`
          }
          ref={refs.current[index]}
          tabIndex={item?.disabled ? -1 : 0}
          aria-disabled={item?.disabled}
          role="listitem"
          onClick={(e) => handleRowClick(item, e)}
          onKeyDown={(e) => {
            if (item?.disabled) return;
            if (e.key === 'Enter' || e.key === ' ') {
              handleRowClick(item, e as any);
            }
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setFocusedIndex((prev) => getNextEnabledIndex(prev, +1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setFocusedIndex((prev) => getNextEnabledIndex(prev, -1));
            }
          }}
        >
          {item.canViewAction && (
            <span
              className="arr-navlist__icon arr-navlist__icon--delete"
              data-testid={`arr-navigation-list-action-button-${dataTestId}-${index}`}
            >
              <ArrIconButton
                icon={actionIcon}
                ariaLabel={actionLabel}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!item?.disabled) onAction?.(item?.id);
                }}
                disabled={item?.disabled}
                tooltipContent={actionLabel}
                tooltipPlacement="top"
                testId={`arr-navigation-list-icon-button-${dataTestId}-${index}`}
              />
            </span>
          )}
          <Typography
            className={`typo-text body3_size ${item?.actionIcon == 'schedule'}? arr-navlist__lableSchdule : arr-navlist__lable`}
            data-testid={`arr-navigation-list-label-${dataTestId}-${index}`}
          >
            {item.label}
          </Typography>
          {item.isCustomReport && item.isShared && (
            <Box className="public-text-box" role="status" aria-live="polite">
              <Typography
                className="public-text"
                aria-label={t('common.public') || 'Public'}
                data-testid={`arr-navigation-list-public-${dataTestId}-${index}`}
              >
                {t('common.public')}
              </Typography>
            </Box>
          )}
        </li>
      ))}
    </ul>
  );
};

export default ArrNavigationList;
